/* eslint-disable no-empty-pattern */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { checkEnableParseJSON } from 'components/helper/helper';
import toast from 'react-hot-toast';
import highSchoolServices from 'services/highSchoolServices';
import { getUserId } from 'utils/sessionStorage';

export interface FamilyState {
  highSchoolData: {
    id: string;
    code: string | number;
    start_date: string;
    end_date: string;
    other: any;
  }[];
  highschoolListObject: any[];
  collegeData: any[];
  myHighSchool: any;
  myCollege: any;
  myFuturePlan: any;
  status: 'idle' | 'loading' | 'failed';
}

const initialState: FamilyState = {
  highSchoolData: [],
  highschoolListObject: [],
  collegeData: [],
  myHighSchool: null,
  myCollege: null,
  myFuturePlan: null,
  status: 'idle',
};
export const getHighSchoolData: any = createAsyncThunk(
  'highSchool/getHighSchoolData',
  async (studentId: any, {rejectWithValue}) => {
    try {
      const response: any = await highSchoolServices.getHighSchool(studentId);
      if (response) {
        const highSchool = checkEnableParseJSON(response.high_school_array);
        return { ...response, highschoolData: highSchool ? [...highSchool]: null };
      }
      else {
        return { ...response, highschoolData: null };
      }
    }
    catch (err) {
      const error = err as AxiosError; 
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
/** Display label for dual-enrollment / college-course college dropdowns. */
export const buildCollegeTookClassLabel = (item: any): string => {
  if (!item) return '';
  if (item.fullName) return String(item.fullName);

  const name = item.college_name || item.school?.name || '';
  const ceeb = item.college_ceeb ?? item.school?.ceeb ?? '';
  const fib = item.school?.fib_code ?? '';
  const state = item.college_state ?? item.school?.state ?? '';
  const city = item.college_city ?? item.school?.city ?? '';
  const country = item.college_country ?? item.school?.country ?? '';

  const parts = [`ceeb: ${ceeb}`];
  if (fib !== undefined && fib !== null && String(fib).trim() !== '') {
    parts.push(`fib code: ${fib}`);
  }
  parts.push(`state: ${state}`, `city: ${city}`, `country: ${country}`);
  return name ? `${name} (${parts.join(', ')})` : '';
};

/** Rematch a saved collegeTookClass string to current dropdown options after label format changes. */
export const matchCollegeTookClassOption = (
  saved: string | undefined | null,
  options: string[]
): string | null => {
  if (!saved || !options?.length) return null;
  if (options.includes(saved)) return saved;

  const savedName = saved.split(' (')[0]?.trim()?.toLowerCase();
  const savedCeeb = saved.match(/ceeb:\s*([^,)]*)/i)?.[1]?.trim();

  for (const opt of options) {
    if (!opt) continue;
    const optName = opt.split(' (')[0]?.trim()?.toLowerCase();
    const optCeeb = opt.match(/ceeb:\s*([^,)]*)/i)?.[1]?.trim();
    if (savedCeeb && optCeeb && savedCeeb === optCeeb) return opt;
    if (savedName && optName && savedName === optName) return opt;
  }
  return null;
};

export const getCollegeData: any = createAsyncThunk(
  'highSchool/getCollegeData',
  async (studentId: any, {rejectWithValue}) => {
    try {
      const response: any = await highSchoolServices.getCollege(studentId);
      // No college_university row yet → empty options (not an error / 404)
      if (!response || typeof response !== 'object') {
        return [];
      }
      const college: any[] =
        checkEnableParseJSON(response.colleges_taken_coursework_array, []) ?? [];
      return college
        .map((item: any) => buildCollegeTookClassLabel(item))
        .filter((label: string) => !!label);
    }
    catch (err) {
      const error = err as AxiosError;
      // Treat missing resource as empty list so Dual Enrollment UI still works
      if (error.response?.status === 404) {
        return [];
      }
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
export const getHighSchool: any = createAsyncThunk(
  'highSchool/getHighSchool',
  async (studentId: any, {rejectWithValue}) => {
    try {
      const response = await highSchoolServices.getHighSchool(studentId);
      return response;
    }
    catch (err) {
      const error = err as AxiosError; 
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
export const updateHighSchool: any = createAsyncThunk(
  'highSchool/updateHighSchool',
  async (payload: any, { getState, rejectWithValue }) => {
    // const state: any = getState();
    // const myHighSchool = state.highSchool.myHighSchool;
    try {
      let myHighSchool = await highSchoolServices.getHighSchool(payload.student_id);
      if (myHighSchool) {
        const response = await highSchoolServices.updateHighSchool(payload);
        return response;
      } else {
        const response = await highSchoolServices.createHighSchool(payload);
        return response;
      }
    }
    catch (err) {
      const error = err as AxiosError; 
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
export const updateHighSchoolFromCourse: any = createAsyncThunk(
  'highSchool/updateHighSchoolFromCourse',
  async (payload: any, { getState, rejectWithValue }) => {
    // const state: any = getState();
    // const myHighSchool = state.highSchool.myHighSchool;
    try {
      let myHighSchool = await highSchoolServices.getHighSchool(payload.student_id);
      if (myHighSchool) {
        const userId = getUserId();
        await highSchoolServices.updateHighSchool({ ...payload, student_id: userId });
        return payload;
      }
    }
    catch (err) {
      const error = err as AxiosError; 
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const getCollege: any = createAsyncThunk('highSchool/getCollege', async (studentId: any, {rejectWithValue}) => {
  try {
    const response = await highSchoolServices.getCollege(studentId);
    // Normalize missing row to empty object (BE may return null/{}).
    return response && typeof response === 'object' ? response : {};
  }
  catch (err) {
    const error = err as AxiosError;
    if (error.response?.status === 404) {
      return {};
    }
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const updateCollege: any = createAsyncThunk(
  'highSchool/updateCollege',
  async (payload: any, { getState, rejectWithValue }) => {
    // const state: any = getState();
    // const myCollege = state.highSchool.myCollege;
    try {
      let myCollege = await highSchoolServices.getCollege(payload.student_id);
      if (myCollege) {
        const response = await highSchoolServices.updateCollege(payload);
        return response;
      } else {
        const response = await highSchoolServices.createCollege(payload);
        return response;
      }
    }
    catch (err) {
      const error = err as AxiosError; 
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
export const getFuturePlan: any = createAsyncThunk(
  'highSchool/getFuturePlan',
  async (studentId: any, {rejectWithValue}) => {
    try {
      const response = await highSchoolServices.getFuturePlan(studentId);
      return response;
    }
    catch (err) {
      const error = err as AxiosError; 
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
export const updateFuturePlan: any = createAsyncThunk(
  'highSchool/updateFuturePlan',
  async (payload: any, { getState, rejectWithValue }) => {
    const state: any = await getState();
    const myFuturePlan = state?.highSchool?.myFuturePlan;
    try {
      if (myFuturePlan) {
        const response = await highSchoolServices.updateFuturePlanThunk(payload);
        return response;
      } else {
        const response = await highSchoolServices.createFuturePlanThunk(payload);
        return response;
      }
    }
    catch (err) {
      const error = err as AxiosError; 
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
export const highSchoolSlice = createSlice({
  name: 'highSchool',
  initialState,
  reducers: {},
  extraReducers: {
    [getHighSchoolData.pending]: (state) => {
      state.status = 'loading';
    },
    [getHighSchoolData.rejected]: (state) => {
      state.status = 'failed';
    },
    [getHighSchoolData.fulfilled]: (state, action) => {
      state.highSchoolData = (action.payload?.highschoolData ?? [])?.map((item: any) => ({
        id: item.high_school_name || item.fullName,
        code:
          item.high_school_code ||
          item.high_school_object?.college_board_code ||
          item.high_school_object?.code,
        start_date: item?.start_date,
        end_date: item?.end_date,
        other: { ...item },
      }));
      state.highschoolListObject = action.payload?.highschoolData ?? [];
      state.myHighSchool = action.payload;
      state.status = 'idle';
    },
    [getCollegeData.pending]: (state) => {
      state.status = 'loading';
    },
    [getCollegeData.rejected]: (state) => {
      state.status = 'failed';
    },
    [getCollegeData.fulfilled]: (state, action) => {
      state.collegeData = action.payload;
      state.status = 'idle';
    },
    //
    [getHighSchool.pending]: (state) => {
      state.status = 'loading';
    },
    [getHighSchool.rejected]: (state) => {
      state.status = 'failed';
    },
    [getHighSchool.fulfilled]: (state, action) => {
      state.myHighSchool = action.payload;
      state.status = 'idle';
    },
    //
    [updateHighSchool.pending]: (state) => {
      state.status = 'loading';
    },
    [updateHighSchool.rejected]: (state) => {
      state.status = 'failed';
    },
    [updateHighSchool.fulfilled]: (state, action) => {
      toast.success(action.payload.message);
      state.myHighSchool = action.payload;
      state.status = 'idle';
    },
    [updateHighSchoolFromCourse.fulfilled]: (state, action) => {
      state.myHighSchool = action.payload;
    },

    //
    [getCollege.pending]: (state) => {
      state.status = 'loading';
    },
    [getCollege.rejected]: (state) => {
      state.status = 'failed';
    },
    [getCollege.fulfilled]: (state, action) => {
      state.myCollege = action.payload;
      state.status = 'idle';
    },
    //
    [updateCollege.pending]: (state) => {
      state.status = 'loading';
    },
    [updateCollege.rejected]: (state) => {
      state.status = 'failed';
    },
    [updateCollege.fulfilled]: (state, action) => {
      toast.success(action.payload.message);
      state.myCollege = action.payload;
      state.status = 'idle';
    },
    //
    [getFuturePlan.pending]: (state) => {
      state.status = 'loading';
    },
    [getFuturePlan.rejected]: (state) => {
      state.status = 'failed';
    },
    [getFuturePlan.fulfilled]: (state, action) => {
      state.myFuturePlan = action.payload;
      state.status = 'idle';
    },
    //
    [updateFuturePlan.pending]: (state) => {
      state.status = 'loading';
    },
    [updateFuturePlan.rejected]: (state) => {
      state.status = 'failed';
    },
    [updateFuturePlan.fulfilled]: (state, action) => {
      toast.success(action.payload.message);
      state.myFuturePlan = action.payload;
      state.status = 'idle';
    },
  },
});

export const {} = highSchoolSlice.actions;
